import { useCallback, useEffect, useRef, useState } from 'react';
import { ConfirmDialog } from '../composites/confirm-dialog';

export interface DiscardConfirmation {
  title: string;
  description: string;
  confirmLabel: string;
}

/** One guard for editor close, tab/entity changes and navigation. Actions run only after explicit discard. */
export function useDiscardGuard({ dirty, busy = false, confirmation }: {
  dirty: boolean;
  busy?: boolean;
  confirmation?: DiscardConfirmation;
}) {
  const pending = useRef<{action:()=>void;onCancel?:()=>void} | null>(null);
  const focus = useRef<HTMLElement | null>(null);
  const discarded = useRef(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!dirty && !busy) return;
    const preventUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', preventUnload);
    return () => window.removeEventListener('beforeunload', preventUnload);
  }, [dirty, busy]);

  const request = useCallback((action: () => void, onCancel?: () => void): boolean => {
    if (busy) return false;
    if (!dirty) { action(); return true; }
    if (!confirmation) return false;
    if (!pending.current) {
      pending.current = {action,onCancel};
      focus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      discarded.current = false;
      setOpen(true);
    }
    return false;
  }, [busy, confirmation, dirty]);

  const prompt = confirmation && <ConfirmDialog
    open={open}
    onOpenChange={(next) => {
      const cancelled=pending.current;
      if (!next) pending.current=null;
      setOpen(next);
      if (!next && !discarded.current) cancelled?.onCancel?.();
    }}
    {...confirmation}
    destructive={false}
    reportBusy={false}
    confirmDisabled={busy}
    returnFocusRef={focus}
    onCloseAutoFocus={(event) => {
      // A closing parent dialog restores its own opener. Never let an unmounted editor steal that focus.
      if (discarded.current && !focus.current?.isConnected) event.preventDefault();
    }}
    onConfirm={() => {
      if (busy) return;
      const action = pending.current?.action;
      pending.current = null;
      discarded.current = true;
      setOpen(false);
      action?.();
    }}
  />;
  return { request, prompt, isConfirming: open };
}
